/**
 * Card Component — Modern Enterprise Design System
 *
 * Clean, minimal card with subtle depth and refined styling.
 * Supports multiple variants: Information, Statistics, Warning, Success, Critical, Clickable, Dashboard.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ── Variant-specific left border styles ───────────────────────────────────────
const VARIANT_BORDER = {
  Warning: '3px solid #f59e0b',
  Success: '3px solid #10b981',
  Critical: '3px solid #ef4444',
};

/**
 * Card component with configurable variant and optional header.
 */
const Card = React.forwardRef(function Card(props, ref) {
  const {
    variant = 'Information',
    title,
    action,
    onClick,
    sx,
    children,
    ...rest
  } = props;

  const isDashboard = variant === 'Dashboard';
  const isClickable = variant === 'Clickable' || !!onClick;
  const leftBorder = VARIANT_BORDER[variant];

  const cardSx = {
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)',
    overflow: 'hidden',
    bgcolor: 'background.paper',
    ...(leftBorder ? { borderLeft: leftBorder } : {}),
    ...(isClickable
      ? {
          cursor: 'pointer',
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 12px -2px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.04)',
            borderColor: 'primary.light',
          },
        }
      : {}),
    ...sx,
  };

  const hasHeader = title || action;
  const hasChildren = React.Children.count(children) > 0;

  return (
    <Box
      ref={ref}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      sx={cardSx}
      {...rest}
    >
      {/* Header */}
      {hasHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: isDashboard ? '12px' : '14px', sm: isDashboard ? '16px' : '20px' },
            py: '14px',
            gap: 1.5,
            ...(hasChildren
              ? {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }
              : {}),
          }}
        >
          {title && (
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {title}
            </Typography>
          )}
          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Box>
      )}

      {/* Content */}
      {hasChildren && (
        <Box sx={{ padding: { xs: isDashboard ? '12px' : '14px', sm: isDashboard ? '16px' : '20px' } }}>
          {children}
        </Box>
      )}
    </Box>
  );
});

export default Card;
